/// <amd-module name="Wit/DeliveryPlans/Common/Components/TeamSettingsTable/TeamSettingsTable" />

import * as React from "react";
import { IObservableArray, IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Dropdown } from "VSSUI/Dropdown";
import { BoltListDragEvent, IListDropData, ListDragDropBehavior, ListDragImage } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { ITableColumn, renderGripper, SimpleTableCell, Table, TableCell } from "VSSUI/Table";
import { DragDropEffect } from "VSSUI/Utilities/DragDrop";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { TableStatus } from "Wit/Common/WitSettingsViews/Components/TableStatus";
import * as Resources from "Wit/DeliveryPlans/Resources";
import { IDeliveryPlansTeamSettingsService } from "./DeliveryPlansTeamSettingsService";
import { ITeamSettingData } from "./TeamSettingsInterfaces";

export interface TeamSettingsTableProps {
    /**
     * Team settings controls will be disabled
     */
    disabled?: boolean;

    /**
     * Optionally show a header for the table with a title and a description
     */
    showHeader?: boolean;
}

namespace TeamSettingsConstants {
    export const maxTeams = 20;
}

export class TeamSettingsTable extends VssComponent<TeamSettingsTableProps> {
    private _teamSettingsService: IDeliveryPlansTeamSettingsService;
    private _listDragDropBehavior: ListDragDropBehavior<ITeamSettingData>;

    constructor(props: TeamSettingsTableProps, context: IVssComponentContext) {
        super(props, context);
        this._teamSettingsService = context.pageContext.getService<IDeliveryPlansTeamSettingsService>("IDeliveryPlansTeamSettingsService");

        this._listDragDropBehavior = new ListDragDropBehavior({
            allowedTypes: ["team-setting"],
            onDragStart: this._onDrag,
            onDrop: this._onDrop,
            renderDragImage: event => {
                const setting = event.detail.dataTransfer.data;
                return <ListDragImage text={this._teamSettingsService.getFriendlyTeamSettingString(setting) || ""} />;
            },
            type: "team-setting"
        });
    }

    public componentDidMount() {
        if (this._teamSettingsService.rows.length === 0) {
            this._teamSettingsService.addTeamSettingsRow();
        }
    }

    public render(): JSX.Element {
        const { disabled, showHeader } = this.props;
        const projectsReady = this._teamSettingsService.projectsReady;
        const rows = this._teamSettingsService.rows;
        return (
            <div className="rhythm-vertical-8">
                {showHeader && (
                    <div className="padding-horizontal-20 rhythm-vertical-8">
                        <div>{Resources.TeamSettingsTableHeader}</div>
                        <div className="secondary-text">{Resources.TeamSettingsTableDescription}</div>
                    </div>
                )}
                <TableStatus
                    className="padding-horizontal-20"
                    dataProviderErrorMessage={this._teamSettingsService.dataProviderErrorMessage}
                    validationErrorMessage={this._teamSettingsService.validationErrorMessage}
                />
                <Observer projectsReady={projectsReady}>
                    {(props: { projectsReady: boolean }) => (
                        <>
                            <Table
                                ariaLabel={Resources.TeamSettingsTableAriaLabel}
                                behaviors={disabled ? undefined : [this._listDragDropBehavior]}
                                className="delivery-plans-settings-table padding-vertical-8"
                                columns={this._columns}
                                itemProvider={rows}
                                renderSpacer={disabled ? undefined : renderGripper}
                            />
                            {!disabled && (
                                <div className="padding-horizontal-20">
                                    <Observer rows={rows}>
                                        {({ rows }: { rows: ITeamSettingData[] }) => (
                                            <Button
                                                disabled={!props.projectsReady || rows.length >= TeamSettingsConstants.maxTeams}
                                                iconProps={{ iconName: "Add" }}
                                                onClick={this._addRow}
                                                text={Resources.AddTeamButtonText}
                                            />
                                        )}
                                    </Observer>
                                </div>
                            )}
                        </>
                    )}
                </Observer>
            </div>
        );
    }

    private _onDrag = (event: BoltListDragEvent<HTMLElement, ITeamSettingData>) => {
        if (!this._teamSettingsService.projectsReady.value) {
            event.detail.dataTransfer.effectAllowed = DragDropEffect.none;
        }
    };

    private _onDrop = (event: BoltListDragEvent<HTMLElement, ITeamSettingData>, dropData: IListDropData) => {
        const item = event.detail.dataTransfer.data;
        const dropIndex = dropData.index - 1;

        // First we remove the item that is being dragged, then we add it in a second operation
        this._teamSettingsService.moveRow(item, dropIndex);
    };

    private _renderProjectColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ITeamSettingData>,
        tableItem: ITeamSettingData
    ): JSX.Element => {
        const onSelect = (event: any, item: IListBoxItem) => {
            this._teamSettingsService.updateProject(rowIndex, item.id);
        };

        const projectsReady = this._teamSettingsService.projectsReady;
        return this._renderDropdownTableCell(
            columnIndex,
            tableItem.id,
            tableItem.projects,
            onSelect,
            projectsReady,
            tableItem.projectSelection,
            tableColumn
        );
    };

    private _renderTeamColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ITeamSettingData>,
        tableItem: ITeamSettingData
    ): JSX.Element => {
        const onSelect = (event: any, item: IListBoxItem) => {
            this._teamSettingsService.updateTeam(rowIndex, item.id);
        };

        return this._renderDropdownTableCell(
            columnIndex,
            tableItem.id,
            tableItem.teams,
            onSelect,
            tableItem.ready,
            tableItem.teamSelection,
            tableColumn
        );
    };

    private _renderBacklogColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ITeamSettingData>,
        tableItem: ITeamSettingData
    ): JSX.Element => {
        const onSelect = (event: any, item: IListBoxItem) => {
            this._teamSettingsService.updateBacklog(rowIndex, item.id);
        };

        return this._renderDropdownTableCell(
            columnIndex,
            tableItem.id,
            tableItem.backlogLevels,
            onSelect,
            tableItem.ready,
            tableItem.backlogSelection,
            tableColumn,
            Resources.BacklogDropdownPlaceholder
        );
    };

    private _renderDropdownTableCell(
        columnIndex: number,
        dataKey: string,
        items: IObservableArray<IListBoxItem> | IListBoxItem[],
        onSelect: (event: any, item: IListBoxItem) => void,
        ready: IObservableValue<boolean>,
        selection: DropdownSelection,
        tableColumn: ITableColumn<ITeamSettingData>,
        placeholder?: string
    ) {
        return (
            <SimpleTableCell columnIndex={columnIndex} key={dataKey + "-col-" + columnIndex} tableColumn={tableColumn}>
                <Observer ready={ready}>
                    {(props: { ready: boolean }) => {
                        return (
                            <Dropdown
                                className="table-dropdown"
                                disabled={this.props.disabled}
                                filterPlaceholderText={Resources.DropdownSearchPlaceholder}
                                items={items}
                                loading={!props.ready}
                                placeholder={!props.ready ? Resources.LoadingText : placeholder}
                                onSelect={onSelect}
                                selection={selection}
                            />
                        );
                    }}
                </Observer>
            </SimpleTableCell>
        );
    }

    private _renderDeleteColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<ITeamSettingData>,
        tableItem: ITeamSettingData
    ): JSX.Element => {
        return (
            <TableCell
                className="bolt-table-cell-side-action"
                columnIndex={columnIndex}
                key={tableItem.id + "-col-" + columnIndex}
                tableColumn={tableColumn}
            >
                <div className="flex-column flex-center">
                    <Button
                        className="bolt-table-cell-content-reveal padding-horizontal-4"
                        disabled={this.props.disabled || this._teamSettingsService.rows.length === 1}
                        iconProps={{ iconName: "Delete" }}
                        onClick={() => this._removeRow(rowIndex)}
                        subtle
                    />
                </div>
            </TableCell>
        );
    };

    private _removeRow = (index: number) => {
        this._teamSettingsService.removeTeamSettingsRow(index);
    };

    private _addRow = () => {
        this._teamSettingsService.addTeamSettingsRow();
    };

    private _onSize = (event: MouseEvent, index: number, width: number) => {
        (this._columns[index].width as ObservableValue<number>).value = width;
    };

    private _columns: ITableColumn<ITeamSettingData>[] = [
        {
            id: "project",
            name: Resources.ProjectColumnLabel,
            onSize: this._onSize,
            renderCell: this._renderProjectColumn,
            width: new ObservableValue(-37)
        },
        {
            id: "team",
            name: Resources.TeamColumnLabel,
            onSize: this._onSize,
            renderCell: this._renderTeamColumn,
            width: new ObservableValue(-37)
        },
        {
            id: "backlog",
            name: Resources.BacklogColumnLabel,
            onSize: this._onSize,
            renderCell: this._renderBacklogColumn,
            width: new ObservableValue(-26)
        },
        {
            id: "delete",
            renderCell: this._renderDeleteColumn,
            width: new ObservableValue(40)
        }
    ];
}
